import { useEffect, useState } from "react";
import { sendMetaLead } from "../analytics/meta";
import { sendLeadMail, submitHubSpotForm } from "../api/hubspot";

// AI導入支援LP（/media/）のCTA遷移先 共通フォームページ。
// LPと同じビジュアル（#2B7FFF / M PLUS Rounded / 角丸）で、診断・資料DL・相談を出し分ける。
// ※送信先(HubSpot等)は未接続。現状は送信でサンキュー表示のみ（TODO: フォーム連携）。

const PRIMARY = "#2B7FFF";
const INK = "#1B2330";
const round = "'M PLUS Rounded 1c', sans-serif";
const PRIVACY_URL = "/privacy";

// ロゴ。以前は usagi AI 名義でうさぎのマークを使っていたが、
// メディアが LEAPS Media 名義になり繋がらなくなったため揃えた。
// マークは一旦サムネイルと同じ丸ドット。本来のロゴが決まったら差し替える。
function BrandMark({ size = 22, color = "#fff" }) {
  const d = Math.round(size * 0.55);
  return (
    <span
      aria-hidden="true"
      style={{
        display: "inline-block",
        width: d,
        height: d,
        borderRadius: "50%",
        background: color,
        flexShrink: 0,
      }}
    />
  );
}

function ensureFonts() {
  if (document.getElementById("ai-lp-fonts")) return;
  const link = document.createElement("link");
  link.id = "ai-lp-fonts";
  link.rel = "stylesheet";
  link.href =
    "https://fonts.googleapis.com/css2?family=M+PLUS+Rounded+1c:wght@400;500;700;800&family=Noto+Sans+JP:wght@400;500;700&display=swap";
  document.head.appendChild(link);
}

function Field({ field, value, onChange }) {
  const id = `f-${field.name}`;
  const base = {
    width: "100%",
    padding: "13px 15px",
    fontSize: 15,
    color: INK,
    background: "#fff",
    border: "1.5px solid #E4E9F1",
    borderRadius: 12,
    outline: "none",
    fontFamily: "inherit",
  };
  return (
    <label style={{ display: "block" }} htmlFor={id}>
      <span
        style={{
          display: "block",
          fontFamily: round,
          fontSize: 13.5,
          fontWeight: 700,
          color: INK,
          marginBottom: 8,
        }}
      >
        {field.label}
        {field.required && <span style={{ color: PRIMARY }}> ＊</span>}
      </span>
      {field.type === "textarea" ? (
        <textarea
          id={id}
          required={field.required}
          rows={4}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ ...base, resize: "vertical" }}
        />
      ) : field.type === "select" ? (
        <select
          id={id}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={base}
        >
          <option value="">選択してください</option>
          {field.options.map((o) => (
            <option key={o} value={o}>
              {o}
            </option>
          ))}
        </select>
      ) : (
        <input
          id={id}
          type={field.type}
          required={field.required}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          style={base}
        />
      )}
    </label>
  );
}

function AiLpFormPage({
  eyebrow,
  title,
  lead,
  fields,
  submitLabel,
  doneTitle,
  doneMsg,
  initialValues,
  alreadySubmitted,
  intro,
  doneExtra,
  leadKind,
  footnote,
  formId,
}) {
  const [values, setValues] = useState(initialValues || {});
  const [submitted, setSubmitted] = useState(false);
  const [agreed, setAgreed] = useState(false);

  // title/meta は App.js が siteMeta.js の ROUTE_META から適用する。
  useEffect(() => {
    ensureFonts();
    window.scrollTo(0, 0);
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!agreed) return;
    // 診断から来た場合(alreadySubmitted)は結果表示時に送信済みなので再送しない（二重送信防止）
    if (!alreadySubmitted) {
      try {
        await submitHubSpotForm({
          // 資料DLなど、専用フォームに送りたいページだけ差し替える
          ...(formId ? { formId } : {}),
          pageName: title,
          fields: {
            email: values.email,
            firstname: values.name,
            company: values.company,
            phone: values.tel,
            message: values.message,
          },
        });
      } catch (err) {
        // 送信失敗でもUXは止めない（サンキュー表示）。詳細はコンソールに。
        console.warn(err);
      }
      // 自動返信と社内通知。HubSpotとは独立していて、
      // 落ちてもフォーム自体は成立させる（awaitしない）。
      if (leadKind) {
        sendLeadMail(leadKind, {
          email: values.email,
          name: values.name,
          company: values.company,
          tel: values.tel,
          message: values.message,
        });
      }
    }
    // Meta側のコンバージョン。HubSpotへの送信可否とは独立に、
    // 「フォームを出し切った」時点で1回だけ送る。
    // 広告の学習とリターゲティング除外（既に取れた人を外す）に使う。
    sendMetaLead({ content_name: title });

    setSubmitted(true);
    window.scrollTo(0, 0);
  };

  return (
    <div
      style={{
        fontFamily: "'Noto Sans JP', system-ui, sans-serif",
        background: "#FFFFFF",
        color: INK,
        lineHeight: 1.7,
        minHeight: "100vh",
      }}
    >
      {/* NAV */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 50,
          background: "rgba(255,255,255,0.9)",
          backdropFilter: "blur(10px)",
          borderBottom: "1px solid #EEF1F6",
        }}
      >
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            height: 70,
            padding: "0 28px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <a
            href="/media/"
            style={{
              display: "flex",
              alignItems: "center",
              gap: 9,
              textDecoration: "none",
            }}
          >
            <span
              style={{
                width: 32,
                height: 32,
                borderRadius: 10,
                background: PRIMARY,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(43,127,255,0.32)",
              }}
            >
              <BrandMark />
            </span>
            <span
              style={{
                fontFamily: round,
                fontWeight: 800,
                fontSize: 18,
                color: INK,
              }}
            >
              LEAPS<span style={{ color: PRIMARY }}> Media</span>
            </span>
          </a>
        </div>
      </div>

      {/* BODY */}
      <div
        style={{
          background:
            "radial-gradient(900px 500px at 78% 8%, #E6F0FF 0%, transparent 55%), #FFFFFF",
        }}
      >
        <div
          style={{ maxWidth: 640, margin: "0 auto", padding: "64px 24px 80px" }}
        >
          {!submitted ? (
            <>
              <div style={{ textAlign: "center", marginBottom: 36 }}>
                <div
                  style={{
                    fontFamily: round,
                    fontSize: 13,
                    fontWeight: 700,
                    color: PRIMARY,
                    marginBottom: 12,
                  }}
                >
                  {eyebrow}
                </div>
                <h1
                  style={{
                    fontFamily: round,
                    fontSize: 32,
                    fontWeight: 800,
                    margin: "0 0 14px",
                  }}
                >
                  {title}
                </h1>
                <p style={{ fontSize: 15, color: "#5B6472", margin: 0 }}>
                  {lead}
                </p>
              </div>
              {/* 資料DLなど、フォームの前に「何が手に入るか」を出したい場合のスロット */}
              {intro}
              <form
                onSubmit={onSubmit}
                style={{
                  background: "#fff",
                  borderRadius: 24,
                  padding: 32,
                  boxShadow: "0 16px 40px rgba(27,35,48,0.08)",
                  border: "1px solid #F0F3F8",
                  display: "flex",
                  flexDirection: "column",
                  gap: 18,
                }}
              >
                {fields.map((f) => (
                  <Field
                    key={f.name}
                    field={f}
                    value={values[f.name] || ""}
                    onChange={(v) => setValues((s) => ({ ...s, [f.name]: v }))}
                  />
                ))}
                <label
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 10,
                    fontSize: 13,
                    color: "#5B6472",
                    cursor: "pointer",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={agreed}
                    onChange={(e) => setAgreed(e.target.checked)}
                    style={{
                      marginTop: 3,
                      width: 16,
                      height: 16,
                      flexShrink: 0,
                    }}
                  />
                  <span>
                    <a
                      href={PRIVACY_URL}
                      target="_blank"
                      rel="noreferrer"
                      style={{ color: PRIMARY, fontWeight: 700 }}
                    >
                      プライバシーポリシー
                    </a>
                    に同意します
                  </span>
                </label>
                <button
                  type="submit"
                  disabled={!agreed}
                  style={{
                    marginTop: 6,
                    fontFamily: round,
                    fontSize: 16,
                    fontWeight: 700,
                    color: "#fff",
                    padding: "16px",
                    borderRadius: 999,
                    background: agreed ? PRIMARY : "#C9D3E3",
                    border: "none",
                    cursor: agreed ? "pointer" : "not-allowed",
                    boxShadow: agreed
                      ? "0 10px 24px rgba(43,127,255,0.34)"
                      : "none",
                  }}
                >
                  {submitLabel}
                </button>
                <p
                  style={{
                    fontSize: 12,
                    color: "#98A1B2",
                    textAlign: "center",
                    margin: 0,
                  }}
                >
                  {footnote ||
                    "自社専用環境／入力データは外部のAI学習に使われません"}
                </p>
              </form>
            </>
          ) : (
            <div
              style={{
                background: "#fff",
                borderRadius: 24,
                padding: "56px 32px",
                textAlign: "center",
                boxShadow: "0 16px 40px rgba(27,35,48,0.08)",
                border: "1px solid #F0F3F8",
              }}
            >
              <div
                style={{
                  width: 56,
                  height: 56,
                  borderRadius: 999,
                  background: "#DCF6E9",
                  color: "#18A86B",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  margin: "0 auto 20px",
                  fontSize: 28,
                  fontWeight: 700,
                }}
              >
                ✓
              </div>
              <h2
                style={{
                  fontFamily: round,
                  fontSize: 24,
                  fontWeight: 800,
                  margin: "0 0 12px",
                }}
              >
                {doneTitle}
              </h2>
              <p style={{ fontSize: 15, color: "#5B6472", margin: 0 }}>
                {doneMsg}
              </p>
              {doneExtra}
            </div>
          )}
        </div>
      </div>

      {/* FOOTER */}
      <div style={{ background: "#F6F9FE", borderTop: "1px solid #EEF1F6" }}>
        <div
          style={{
            maxWidth: 1120,
            margin: "0 auto",
            padding: "28px",
            textAlign: "center",
            fontSize: 12,
            color: "#98A1B2",
          }}
        >
          運営会社：株式会社LEAPS ／ 東京都千代田区神田神保町1-41 ／ © 2026
          Leaps Inc.
        </div>
      </div>
    </div>
  );
}

export default AiLpFormPage;
